import { ExternalReturnHubType } from "@redotech/account-common/address";
import { Channel } from "@redotech/congo-enums/channel";
import { CompensationMethodType } from "@redotech/congo-enums/compensation-method-type";
import { DraftReturnSource } from "@redotech/congo-enums/draft-return-source";
import { LabelPayerReason } from "@redotech/congo-enums/label-payer-reason";
import { MerchantPpPlusClaimMode } from "@redotech/congo-enums/merchant-pp-plus-claim-mode";
import { Payer } from "@redotech/congo-enums/payer";
import { ProvisionType } from "@redotech/congo-enums/provision-type";
import { ReviewingParty } from "@redotech/congo-enums/reviewing-party";
import { ReviewingPartyReason } from "@redotech/congo-enums/reviewing-party-reason";
import { ThirdPartyRefundProvider } from "@redotech/congo-enums/third-party-refund-provider";
import { UpsellBillingTrigger } from "@redotech/congo-enums/upsell-billing-trigger";
import { Currency } from "@redotech/money/currencies";
import { FlattenedCustomerVariantPreferencesSchema } from "@redotech/redo-agents-cards/returns-agents/exchange-agent/customer-variant-preferences";
import { InteractiveMapLocationSchema } from "@redotech/redo-common-model-location-common/interactive-map-location";
import { PriceSetSchema } from "@redotech/redo-common-model-money-common/price-set";
import { ShippingRateTableSchema } from "@redotech/redo-conversion-core-common/redo-in-shipping/shipping-rate-table";
import {
  AddressSchema as ShipmentAddressZodSchema,
  ShipmentParcelZodSchema,
  StoredShippingProviderTypeSchema,
} from "@redotech/redo-fulfillment-carrier-common/shipment";
import { Provider } from "@redotech/redo-order-common/provider";
import { AccountingVersion } from "@redotech/redo-return-core-common/accounting-version";
import { DateFromDateOrIsoStringSchema } from "@redotech/redo-return-core-common/draft-return/date-schema";
import { ShipmentMethodName } from "@redotech/redo-return-core-common/draft-return/draft-return-enums";
import {
  AdvancedExchangeItemSchema,
  ClaimDocumentSchema,
  NewExchangeItemSchema,
  NonReturnableItemSchema,
  PendingReturnItemSchema,
  ReturnableItemSchema,
} from "@redotech/redo-return-core-common/draft-return/draft-return-items";
import { DropoffLocationResultSchema } from "@redotech/redo-return-core-common/dropoff/dropoff-types";
import { ReturnsAccountingZodSchema } from "@redotech/redo-return-core-common/exchange-group";
import {
  excessExchangeValueMethods,
  leftoverExchangeValueSelectionSources,
} from "@redotech/redo-return-core-common/exchange-settings";
import {
  AdjustmentZodSchema,
  LocaleZodSchema,
} from "@redotech/redo-return-core-common/return";
import { PickupLocation } from "@redotech/redo-return-core-common/return-enums";
import { PickupPayer } from "@redotech/redo-return-core-common/return-flow-enums";
import {
  InputAnswerPartWithStepSchema,
  RepairFeeConfigZodSchema,
} from "@redotech/redo-return-core-common/return-flow-schema";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import { AllocationRecordSchema } from "@redotech/redo-return-core-common/returns/allocation-record";
import { NewOrderDiscountDetailsSchema } from "@redotech/redo-return-core-common/returns/new-order-discount-details";
import { SerializedExchangeItemTotalsSchema } from "@redotech/redo-return-core-common/returns/return-totals/types";
import { ReturnCarrierOptionSchema } from "@redotech/redo-return-core-common/shipping/return-carrier-option";
import { ReturnRateSourceSelectionSchema } from "@redotech/redo-return-core-common/shipping/return-rate-source-selection-schema";
import { RecipientOrderMatch } from "@redotech/redo-return-core-common/third-party-refunds/recipient";
import { dropoffProviderType } from "@redotech/redo-return-dropoff-provider-common/dropoff-provider";
import { RepairFeeCollectionStrategy } from "@redotech/redo-return-warranties-common/warranties";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

/**
 * Steps in the MCP create-return state machine flow.
 * Used by merchant-created returns via the public MCP server.
 */
export const DraftReturnStep = [
  "select_items",
  "walk_flow",
  "compensation",
  "overrides",
  "shipment",
  "exchange",
  "ready",
] as const;
export type DraftReturnStep = (typeof DraftReturnStep)[number];

export const COMPENSATION_TYPES = Object.values(CompensationMethodType);

/** A flat exchange shipping rate from a finalize-flow block, priced in the shop currency */
export const ExchangeShippingRateSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullish(),
  price: z.number(),
});
export type ExchangeShippingRate = z.infer<typeof ExchangeShippingRateSchema>;

export const CompensationMethodSchema = z.object({
  type: createEnum(CompensationMethodType),
  // keys are pendingItemIds
  itemValues: z.record(
    z.string(),
    z.object({
      strategy: createEnum(["refund", "storeCredit", "repair"]),
      value: zExt.big(),
      exchangeCredit: zExt.big(),
      isVariantExchange: z.boolean(),
      tax: zExt.big(),
      chargeRepairFee: z.boolean().nullish(),
      repairFee: RepairFeeConfigZodSchema.nullish(),
      usesExcessExchangeValue: z.boolean().nullish(),
      /** Pre-discount item price × quantity; basis for percentage repair fees. */
      preDiscountPrice: zExt.big().nullish(),
    }),
  ),
  totalReturnableValue: zExt.big(),
  // keys are pendingItemIds
  /** @deprecated use returnLevelAdjustments and itemLevelAdjustments instead */
  adjustments: z.record(z.string(), zExt.big()),
  returnLevelAdjustments: z.array(AdjustmentZodSchema).nullish(),
  itemLevelAdjustments: z
    .record(z.string(), z.array(AdjustmentZodSchema))
    .nullish(),
  shippingRequired: z.record(z.string(), z.boolean()),
  manualReview: z.boolean(),
  manualReviewReason: z.string().nullish(),
  flagged: z.boolean(),
  flaggedReason: z.string().nullish(),
  payer: createEnum(Payer),
  reason: createEnum(LabelPayerReason).nullish(),
  returnMethodRequired: z.boolean(),
  exchangeShippingName: z.string().nullish(),
  /** Set when a finalize-flow block was traversed; replaces the team's rate tables */
  exchangeShippingRates: z.array(ExchangeShippingRateSchema).nullish(),
  exchangeOrderTags: z.array(z.string()).nullish(),
  chargeNewOrderShipping: z.boolean(),
  discountCodeName: z
    .object({ prefix: z.string().nullish(), suffix: z.string().nullish() })
    .nullish(),
  discountTitle: z.string().nullish(),
  advancedExchangeAvailable: z.boolean(),
  itemsToRefundPoints: z.array(z.string()).nullish(),
  itemsToDeductPoints: z.array(z.string()).nullish(),
  tags: z.array(z.object({ name: z.string(), color: z.string() })).nullish(),
  coverPackagePickup: z.boolean().nullish(),
  leftoverExchangeValueSelection: z
    .object({
      method: createEnum(excessExchangeValueMethods),
      source: createEnum(leftoverExchangeValueSelectionSources),
    })
    .nullish(),
});

export type CompensationMethod = z.infer<typeof CompensationMethodSchema>;
export type CompensationType = CompensationMethod["type"];

// Enum definitions
export const ExchangeTypeContext = createEnum(["VARIANT", "ADVANCED"]);
export const PickupLocationEnum = createEnum(PickupLocation);
export const AdjustmentType = createEnum(["percentage", "amount"]);
export const ReturnOrClaimType = createEnum(["return", "claim"]);
export const ReturnRequestStatus = createEnum(["PENDING", "REJECTED"]);

// Schema definitions
const AddressSchema_ = z.object({
  name: z.string(),
  email: z.string(),
  street1: z.string(),
  street2: z.string().nullish(),
  state: z.string().nullish(),
  city: z.string(),
  country: z.string(),
  zip: z.string(),
  phone: z.string().nullish(),
  returnerName: z.string().nullish(),
  returnerEmail: z.string().nullish(),
  _id: z.string().nullish(),
});
export const AddressSchema: z.ZodType<Address> = AddressSchema_;
export interface Address extends z.infer<typeof AddressSchema_> {}

const PickupDataSchema_ = z.object({
  email: z.string().nullish(),
  phone: z.string().nullish(),
  pickupDate: z.string(),
  pickupLocation: z.object({
    packageLocation: PickupLocationEnum,
    specialInstructions: z.string().nullish(),
  }),
  textReminder: z.boolean(),
  pickupPayer: createEnum(PickupPayer).nullish(),
});
const PickupDataSchema: z.ZodType<PickupData> = PickupDataSchema_;
export interface PickupData extends z.infer<typeof PickupDataSchema_> {}

export const ShipmentMethodSchema = z.union([
  z.object({
    method: z.literal(ShipmentMethodName.PICKUP),
    pickupData: PickupDataSchema,
  }),
  z.object({ method: z.literal(ShipmentMethodName.IN_STORE) }),
  z.object({ method: z.literal(ShipmentMethodName.SETTLEMENT) }),
  z.object({ method: z.literal(ShipmentMethodName.HAPPY_RETURNS) }),
  z.object({ method: z.literal(ShipmentMethodName.BLUE_YONDER) }),
  z.object({ method: z.literal(ShipmentMethodName.RETURN_BEAR) }),
  z.object({ method: z.literal(ShipmentMethodName.SELF_SHIP) }),
  z.object({ method: z.literal(ShipmentMethodName.MANUAL_LABEL) }),
  z.object({ method: z.literal(ShipmentMethodName.DROPOFF) }),
]);
export type ShipmentMethod = z.infer<typeof ShipmentMethodSchema>;

const SettlementSchema_ = z.object({
  accepted: z.boolean(),
  refund: z.number(),
});
export const SettlementSchema: z.ZodType<Settlement> = SettlementSchema_;
export interface Settlement extends z.infer<typeof SettlementSchema_> {}

const discountNameSchema_ = z.object({
  title: z.string(),
  code: z.string(),
  type: z.string().optional(),
});
export const discountNameSchema: z.ZodType<DiscountName> = discountNameSchema_;
export interface DiscountName extends z.infer<typeof discountNameSchema_> {}

const ShipmentAddressSchema_ = z.object({
  name: z.string().nullish(),
  street1: z.string().nullish(),
  street2: z.string().nullish(),
  city: z.string().nullish(),
  state: z.string().nullish(),
  zip: z.string().nullish(),
  country: z.string().nullish(),
  country_name: z.string().nullish(),
  phone: z.string().nullish(),
  id: z.string().nullish(),
  externalLocationId: z.string().nullish(),
  trackstarWarehouseId: z.string().nullish(),
  inmarLocationId: z.string().nullish(),
  blueYonderLocationId: z.string().nullish(),
  aims360WarehouseId: z.string().nullish(),
  isSekoHub: z.boolean().nullish(),
  _id: zExt.objectId().nullish(),
  returnsCenterId: z.string().nullish(),
  externalReturnHub: z
    .object({
      provider: z.enum(ExternalReturnHubType),
      locationId: z.string().nullish(),
    })
    .nullish(),
});
export const ShipmentAddressSchema: z.ZodType<ShipmentAddress> =
  ShipmentAddressSchema_;
export interface ShipmentAddress extends z.infer<
  typeof ShipmentAddressSchema_
> {}

const HappyReturnsLocationSchema_ = z.object({
  name: z.string(),
  address: z.object({
    address: z.string(),
    city: z.string(),
    state: z.string(),
    zipcode: z.string(),
  }),
  distance: z.number(),
  lat: z.number(),
  lng: z.number(),
});
export const HappyReturnsLocationSchema: z.ZodType<HappyReturnsLocation> =
  HappyReturnsLocationSchema_;
export interface HappyReturnsLocation extends z.infer<
  typeof HappyReturnsLocationSchema_
> {}

const HappyReturnsDetailsSchema_ = z.object({
  price: zExt.big(),
  locations: z.array(HappyReturnsLocationSchema),
  zipCode: z.string(),
  retailerId: z.string(),
});
export const HappyReturnsDetailsSchema: z.ZodType<HappyReturnsDetails> =
  HappyReturnsDetailsSchema_;
export interface HappyReturnsDetails extends z.infer<
  typeof HappyReturnsDetailsSchema_
> {}

const BlueYonderLocationSchema_ = z.object({
  blueyonderLocationId: z.string(),
  locationName: z.string(),
  address: z.object({
    street1: z.string(),
    street2: z.string().optional(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
    country: z.string(),
  }),
  distance: z.string(),
  outsideConfiguredRadius: z.boolean().optional(),
  configuredRadiusMiles: z.number().optional(),
  geo: z.object({ latitude: z.number(), longitude: z.number() }),
});

export const BlueYonderLocationSchema: z.ZodType<BlueYonderLocation> =
  BlueYonderLocationSchema_;
export interface BlueYonderLocation extends z.infer<
  typeof BlueYonderLocationSchema_
> {}

const BlueYonderDetailsSchema_ = z.object({
  locations: z.array(BlueYonderLocationSchema),
  retailerCompanyId: z.string(),
});
export const BlueYonderDetailsSchema: z.ZodType<BlueYonderDetails> =
  BlueYonderDetailsSchema_;
export interface BlueYonderDetails extends z.infer<
  typeof BlueYonderDetailsSchema_
> {}

const DropoffLocationSchema_ = z.object({
  id: z.string(),
  name: z.string(),
  address: z.object({
    street1: z.string(),
    street2: z.string().optional(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
    country: z.string(),
  }),
  distance: z.string().describe("e.g., '1.2 miles', MUST BE IN MILES"), // e.g., "1.2 miles"
  geo: z.object({ latitude: z.number(), longitude: z.number() }),
});

export const DropoffLocationSchema: z.ZodType<DropoffLocation> =
  DropoffLocationSchema_;
export interface DropoffLocation extends z.infer<
  typeof DropoffLocationSchema_
> {}

/**
 * @deprecated Legacy direct-ReturnBear flow. Retained for existing draft
 * returns; not written by new draft returns. Do not add new readers.
 */
const ReturnBearDetailsSchema_ = z.object({
  returnBearCaseId: z.string(),
  returnBearCaseShortId: z.string(),
  returnBearExternalRMAReferenceId: z.string().nullish(),
  returnBearCaseVersion: z.number().nullish(),
  locations: z.array(DropoffLocationSchema),
  shippingLabelAvailability: z.boolean(),
  /**
   * @deprecated Use dropoffFees on the Return flow step instead.
   * Kept as fallback for teams without a configured flat rate.
   */
  flatRateFee: z.number().nullish(),
  /**
   * @deprecated Use dropoffFees on the Return flow step instead.
   * The base rate from the ReturnBear rate table before markup.
   */
  rate: z.number().nullish(),
  /**
   * @deprecated Use dropoffFees on the Return flow step instead.
   * The configured markup applied on top of the base rate.
   */
  markup: z.number().nullish(),
});

export const ReturnBearDetailsSchema: z.ZodType<ReturnBearDetails> =
  ReturnBearDetailsSchema_;
export interface ReturnBearDetails extends z.infer<
  typeof ReturnBearDetailsSchema_
> {}

const ParcelDimsSchema_ = z.object({
  lengthInInches: z.number().nullish(),
  widthInInches: z.number().nullish(),
  heightInInches: z.number().nullish(),
});
export const ParcelDimsSchema: z.ZodType<ParcelDims> = ParcelDimsSchema_;
export interface ParcelDims extends z.infer<typeof ParcelDimsSchema_> {}

const ParcelProductSchema_ = z.object({
  grams: z.number().nullish(),
  quantity: z.number().nullish(),
  dims: ParcelDimsSchema.nullish(),
});
export const ParcelProductSchema: z.ZodType<ParcelProduct> =
  ParcelProductSchema_;
export interface ParcelProduct extends z.infer<typeof ParcelProductSchema_> {}

const CorrectedProductSchema_ = z.object({
  variantId: z.string().nullish(),
  productId: z.string().nullish(),
  grams: z.number().nullish(),
  dims: ParcelDimsSchema.nullish(),
});
export const CorrectedProductSchema: z.ZodType<CorrectedProduct> =
  CorrectedProductSchema_;
export interface CorrectedProduct extends z.infer<
  typeof CorrectedProductSchema_
> {}

const DraftReturnShipmentSchema_ = z.object({
  carrier: z.string(),
  fee: z.string(),
  flatRateFee: z.string().nullish(),
  presentmentFlatRateFee: z.string().nullish(),
  dropoffFlatRateFees: z
    .array(
      z.object({
        provider: z.enum(dropoffProviderType),
        fee: z.string(),
        /**
         * Pre-computed presentment-currency amount for this drop-off fee, set
         * by the shipping estimate handler when the winning per-item override
         * (or the base fee via live conversion) is resolved. Read directly by
         * `get-shipping-fee.ts` to avoid re-running currency conversion.
         */
        presentmentFee: z.string().nullish(),
        currencyOverrides: z.record(z.string(), z.number()).nullish(),
        perCurrencyAmountsEnabled: z.boolean().nullish(),
      }),
    )
    .default([]),
  /** Shop currency; prefer `pickupFeeSet`, which also carries presentment. */
  pickupFee: z.string().nullish(),
  pickupFeeSet: PriceSetSchema.nullish(),
  pickupIneligibility: z.string().nullish(),
  pickupCoveredByMerchant: z.boolean().nullish(),
  address: ShipmentAddressSchema,
  pendingReturnItems: z.array(z.string()),
  happyReturnsDetails: HappyReturnsDetailsSchema.nullish(),
  blueYonderDetails: BlueYonderDetailsSchema.nullish(),
  /** @deprecated Legacy direct-ReturnBear flow; retained for existing draft returns, not written by new ones. */
  returnBearDetails: ReturnBearDetailsSchema.nullish(),
  dropoffs: z.array(DropoffLocationResultSchema).default([]),
  inStoreLocations: z.array(InteractiveMapLocationSchema).nullish(),
  shippingProviderRate: z.string().nullish(),
  merchantUpcharge: z.string().nullish(),
  /** The label upcharge/downcharge applied by merchant settings (flat or percentage) */
  merchantAppliedUpcharge: z.string().nullish(),
  customerUpchargeFee: z.number().nullish(),
  marketRate: z.string().nullish(),
  calculatedMarketRate: z
    .object({
      marketRate: z.string(),
      carrier: z.string(),
      serviceLevel: z.string(),
      listedRate: z.string(),
      defaultDiscountPercent: z.number(),
      teamDiscountPercent: z.number(),
    })
    .nullish(),
  /**
   * Insurance cost for the shipment, calculated based on product prices.
   * This is charged to customers when shippingInsurance is enabled on the team.
   * Redo accepts liability for insurance claims rather than purchasing carrier insurance.
   */
  insuranceCost: z.number().nullish(),
  rateSource: z
    .object({
      isMerchantCarrier: z.boolean().nullish(),
      shippingProviderType: StoredShippingProviderTypeSchema.nullish(),
      isFromAlternativeAccount: z.boolean().nullish(),
    })
    .nullish(),
  shipmentId: z.string().nullish(),
  rateSourceSelection: ReturnRateSourceSelectionSchema.nullish(),
  shipmentParcel: ShipmentParcelZodSchema.nullish(),
  customerAddress: ShipmentAddressZodSchema.nullish(),
  parcelProduct: ParcelProductSchema.nullish(),
  correctedProducts: z.array(CorrectedProductSchema).nullish(),
  estimatedAt: z.date().nullish(),
  donationMatchIds: z.array(z.string()).nullish(),
  carrierOptions: z.array(ReturnCarrierOptionSchema).nullish(),
  coveredCarrierOptionId: z.string().nullish(),
  selectedCarrierUpgradeFee: z.string().nullish(),
});
export const DraftReturnShipmentSchema: z.ZodType<DraftReturnShipment> =
  DraftReturnShipmentSchema_;
export interface DraftReturnShipment extends z.infer<
  typeof DraftReturnShipmentSchema_
> {}

const PriceRangeSchema = z.tuple([z.number(), z.number()]);
export type PriceRange = z.infer<typeof PriceRangeSchema>;

const ReturnTotalCalculationsSchema_ = z.object({
  pickupAccept: z.boolean(),
  /** Presentment currency, like the other display totals. */
  pickupFee: z.number().nullish(),
  pickupFeeSet: PriceSetSchema.nullish(),
  inStoreAccept: z.boolean(),
  settlementAccept: z.boolean(),
  settlementRefund: z.number(),
  shippingFee: z.number(),
  /**
   * Shopper-owed upgrade for a pricier carrier, shown as its own line. Part of
   * `totalShippingFee`, deducted or charged with the label fee.
   */
  carrierUpgradeFeeComponent: z.number().optional(),
  customerUpchargeFee: z.number().optional(),
  exchangeShippingFee: z.number(),
  totalShippingFee: z.number(),
  /** Mirror of ReturnTotals.chargesExchangeShippingViaStripe — when true the
   * exchange shipping fee is collected via Stripe at submit and included in
   * `paymentOwed`/`totalPaymentOwed`; when false it's folded into
   * `paymentOwed`/`totalPaymentOwed` because it rides on the draft order. */
  chargesExchangeShippingViaStripe: z.boolean().optional(),
  labelDeducted: z.boolean(),
  canDeductLabel: z.boolean().nullish(),
  returnValue: z.number(),
  totalTaxes: z.number(),
  /** Refunded duties, including their tax. Zero when not refunded. */
  totalDuties: z.number().optional(),
  newOrderValue: z.number(),
  totalNewOrderTaxes: z.number(),
  totalAdjustmentValue: z.number(),
  /** Net merchant-manual fee/bonus that is NOT already folded into
   * `returnValue` (the displayed pre-adjustment item value). Surfaced so the
   * review/summary adjustment line reflects merchant-created fees and bonuses,
   * which are excluded from `totalAdjustmentValue` (return-flow adjustments
   * only). */
  totalMerchantAdjustment: z.number().optional(),
  totalReturnValue: z.number(),
  totalStoreCredit: z.number(),
  totalRefund: z.number(),
  totalRepairFee: PriceRangeSchema,
  hasGreenReturnItems: z.boolean(),
  hasNonGreenReturnItems: z.boolean(),
  showNewItems: z.boolean(),
  showNewOrderAddress: z.boolean(),
  paymentOwed: z.number(),
  totalPaymentOwed: z.number(),
  paymentOwedRange: PriceRangeSchema.nullish(),
  skipPayment: z.boolean(),
  totalLoyaltyPointsDeducted: z.number(),
  totalLoyaltyPointsRefunded: z.number(),
  totalReturnValueDeductedDueToLoyaltyPoints: z.number(),
  totalOrderShipping: z.number(),
  newOrderDiscount: z.number().nullish(),
  merchantCarrierShippingFee: z.number().optional(),
  preDiscountShopNowBonus: z.number().optional(),
  unusedPreDiscountShopNowBonus: z.number().optional(),
  combinedUnusedUseItOrLoseItBonuses: z.number().optional(),
  /** The engine's postings and accounts, once the draft carries a version stamp. */
  allocation: AllocationRecordSchema.nullish(),
  /**
   * Every exchange line as the calculator priced it, the same list a return's
   * summary totals carry. A Shop Now line is keyed like `newOrderData.items`,
   * `<variantId>-<index>`. Readers price a line from here only where the
   * engine settled it (`settledBy`); every other line reads the document.
   * Optional for payloads from a backend predating it.
   */
  exchangeItemTotals: z.array(SerializedExchangeItemTotalsSchema).optional(),
});
const ReturnTotalCalculationsSchema: z.ZodType<ReturnTotalCalculations> =
  ReturnTotalCalculationsSchema_;
export interface ReturnTotalCalculations extends z.infer<
  typeof ReturnTotalCalculationsSchema_
> {}

const ReturnTotalCalculationsOutputSchema_ = z.object({
  instant: ReturnTotalCalculationsSchema,
  processed: ReturnTotalCalculationsSchema,
});
export const ReturnTotalCalculationsOutputSchema: z.ZodType<ReturnTotalCalculationsOutput> =
  ReturnTotalCalculationsOutputSchema_;
export interface ReturnTotalCalculationsOutput extends z.infer<
  typeof ReturnTotalCalculationsOutputSchema_
> {}

const LineItemSchema_ = z.object({ id: z.string(), exchangeCount: z.number() });
const LineItemSchema: z.ZodType<LineItem> = LineItemSchema_;
export interface LineItem extends z.infer<typeof LineItemSchema_> {}

const OrderSchema_ = z.object({
  id: zExt.objectId(),
  warrantyId: zExt.objectId().nullish(),
  purchaseChannelName: z.string().nullish(),
  name: z.string(),
  tags: z.array(z.string()),
  note: z.string().nullish(),
  noteAttributes: z
    .array(
      z.object({ name: z.string().nullish(), value: z.string().nullish() }),
    )
    .nullish(),
  orderDate: z.date(),
  totalPrice: z.string(),
  currency: z.string(),
  protected: z.boolean(),
  packageProtected: z.boolean(),
  finalSaleReturnsProtected: z.boolean(),
  productsCoveredByFinalSale: z.array(z.string()),
  paymentMethods: z.array(z.string()),
  salesChannel: z.string(),
  exchangeCount: z.number().nullish(),
  discounts: z.array(discountNameSchema),
  platform: createEnum(Provider),
  sourceNotes: z
    .union([z.string(), z.array(InputAnswerPartWithStepSchema)])
    .nullish(),
  coverage: z.object({
    exchange: z.boolean(),
    storeCredit: z.boolean(),
    refund: z.boolean(),
    repair: z.boolean(),
  }),
  lineItems: z.array(LineItemSchema).nullish(),
});
const OrderSchema: z.ZodType<Order> = OrderSchema_;
export interface Order extends z.infer<typeof OrderSchema_> {}

const NewOrderDataSchema_ = z.object({
  items: z.array(AdvancedExchangeItemSchema),
  /** @deprecated use totalPriceSet instead */
  totalPrice: z.string(), // without tax
  totalPriceSet: PriceSetSchema.nullish(),
  /** @deprecated use totalTaxSet instead */
  totalTax: z.string(),
  totalTaxSet: PriceSetSchema.nullish(),
  taxLineInfo: z
    .array(z.object({ title: z.string(), rate: z.number() }))
    .nullish(),
  discountCodes: z.array(z.string()),
  discountAllocations: z.array(
    z.object({
      /** @deprecated use discountedAmountSet instead */
      discountedAmount: z.object({
        amount: z.string(),
        currencyCode: z.string(),
      }),
      discountedAmountSet: PriceSetSchema.nullish(),
    }),
  ),
  discountDetails: NewOrderDiscountDetailsSchema.nullish(),
  blockAutomaticDiscounts: z.boolean().nullish(),
});
export const NewOrderDataSchema: z.ZodType<NewOrderData> = NewOrderDataSchema_;
export interface NewOrderData extends z.infer<typeof NewOrderDataSchema_> {}

export enum ExchangeShippingOptionSource {
  TeamRateTable = "team_rate_table",
  FinalizeFlow = "finalize_flow",
}

export const ExchangeShippingOptionSchema = z.object({
  /**
   * Stable option id: the team rate table id or the finalize-flow rate id.
   * Options persisted before this field existed have none.
   */
  id: z.string().nullish(),
  source: createEnum(ExchangeShippingOptionSource).nullish(),
  /** Only set for options sourced from a team rate table */
  rateTable: z.lazy(() => ShippingRateTableSchema).nullish(),
  name: z.string(),
  price: z.number(),
  currency: createEnum(Currency),
  priceSet: PriceSetSchema.nullish(),
  description: z.string().nullish(),
});

export type ExchangeShippingOption = z.infer<
  typeof ExchangeShippingOptionSchema
>;

/**
 * Options persisted before `id` existed only carry their rate table, whose id
 * is what the server now uses as the option id.
 */
export function getExchangeShippingOptionId(
  option: ExchangeShippingOption,
): string {
  return option.id ?? option.rateTable?._id?.toString() ?? option.name;
}

// Main ReturnRequest schema
const DraftReturnSchema_ = z.object({
  _id: zExt.objectId(),
  teamId: zExt.objectId(),
  treatmentHash: z.string(),
  flow: z.any(),
  finalizeFlow: z.any().optional(),
  locationFlow: z.any().nullish(),
  labelFlow: z.any().nullish(),
  returnType: createEnum(ReturnTypeEnum),
  /** The returns-accounting stamp; absent means pre-allocation. */
  accountingVersion: createEnum(AccountingVersion, {
    enumName: "AccountingVersion",
  }).nullish(),
  /**
   * What the returns-accounting engine froze as items entered the draft, so
   * a stamped draft prices from what the shopper was shown.
   */
  accounting: ReturnsAccountingZodSchema.nullish(),
  orders: z.array(OrderSchema), // I wish this was a Record<string, OrderSchema>. I wish mongoose supported this in a way that wasn't a map. plz lmk if you know how to do this
  customer: z.object({
    id: z.string().nullish(),
    shippingAddress: AddressSchema.nullish(),
    name: z.string(),
    tags: z.array(z.string()),
    /** Customer's current tags fetched live at draft time (for TIME_OF_RETURN customer-tag conditions). */
    liveTags: z.array(z.string()).nullish(),
    numCustomerClaims: z.number(),
    numCustomerReturns: z.number(),
    numOrders: z.number(),
    // Add dates for time-based conditions
    orderDates: z.array(DateFromDateOrIsoStringSchema).nullish(),
    returnDates: z.array(DateFromDateOrIsoStringSchema).nullish(),
    returnRate: z.number(),
    newOrderAddress: AddressSchema.nullish(),
    timezone: z.string().nullish(),
    // This is an email that is set by the merchant during merchant created returns when the customer does not have an email on file.
    updatedEmail: z.string().nullish(),
    variantPreferences: FlattenedCustomerVariantPreferencesSchema.nullish(),
    redoCustomerId: z.string().nullish(),
    locale: LocaleZodSchema.nullish(),
  }),
  giftRecipient: z.object({ locale: LocaleZodSchema }).nullish(),
  featureFlags: z.record(z.string(), z.boolean()).nullish(),
  newOrderItems: z.array(NewExchangeItemSchema),
  newOrderData: NewOrderDataSchema.nullish(),
  returnableItems: z.array(ReturnableItemSchema),
  nonReturnableItems: z.array(NonReturnableItemSchema).default([]),
  pendingReturnItems: z.array(PendingReturnItemSchema),
  compensationMethods: z.array(CompensationMethodSchema),
  thirdPartyRefundProvider: createEnum(ThirdPartyRefundProvider).nullish(),
  thirdPartyRefundRecipient: z
    .object({
      email: z.string().nullish(),
      phone: z.string().nullish(),
      providerAccountId: z.string().nullish(),
      /** ISO 3166-1 alpha-2, uppercase. Null predates cross-border and means "US". */
      providerAccountCountry: z.string().nullish(),
      matchedOrderContact: createEnum(RecipientOrderMatch).nullish(),
    })
    .nullish(),
  selectedMethod: createEnum(CompensationMethodType).nullish(),
  shipmentMethod: ShipmentMethodSchema.nullish(),
  shipments: z.array(DraftReturnShipmentSchema).nullish(),
  nearestDropoffDistanceMiles: z.number().nullish(),
  // Contains compensation level adjustments for the selected compensation method
  adjustments: z.array(AdjustmentZodSchema).nullish(),
  itemLevelAdjustments: z
    .record(z.string(), z.array(AdjustmentZodSchema))
    .nullish(),
  submitted: z.boolean().nullish(),
  rejected: z.boolean().nullish(),
  provisionType: createEnum(ProvisionType),
  currency: createEnum(Currency).nullish(),
  presentmentCurrency: createEnum(Currency).nullish(),
  pickupVariant: z.string().nullish(),
  referralId: z.string().nullish(),
  merchantCreated: z.boolean().nullish(),
  merchantName: z.string().nullish(),
  waiveCustomerCompensation: z.boolean().nullish(),
  waiveOutstandingBalance: z.boolean().nullish(),
  keepFeesAndBonuses: z.boolean().nullish(),
  merchantRoles: z.array(z.string()).nullish(),
  merchantPpPlusClaimMode: createEnum(MerchantPpPlusClaimMode).nullish(),
  repairFeeCollectionStrategy: createEnum(
    RepairFeeCollectionStrategy,
  ).nullish(),
  tnplContractId: zExt.objectId().nullish(),
  upsellBillingTrigger: createEnum(UpsellBillingTrigger).nullish(),
  provider: createEnum(Provider).nullish(),
  channel: createEnum(Channel).nullish(),
  pos: z.object({ locationId: z.string() }).nullish(),
  /** @deprecated use currentShopToPresentmentExchangeRate */
  exchangeRate: z.number().optional(),
  currentShopToPresentmentExchangeRate: z.number().optional(),
  currentUsdToPresentmentExchangeRate: z.number().optional(),
  selectedExchangeShippingOption: ExchangeShippingOptionSchema.nullish(),
  source: createEnum(DraftReturnSource).nullish(),
  newItemToExchangeRate: z.number().optional(),
  returnTags: z
    .array(z.object({ name: z.string(), color: z.string() }))
    .optional(),
  reviewingParty: createEnum(ReviewingParty).nullish(),
  claimIdDocuments: z.array(ClaimDocumentSchema).nullish(),
  claimSupportingDocuments: z.array(ClaimDocumentSchema).nullish(),
  reviewingPartyReason: createEnum(ReviewingPartyReason).nullish(),
  isGiftReturn: z.boolean().nullish(),
  bundleParentProducts: z.record(z.string(), ReturnableItemSchema).nullish(),
  bundleTogetherState: z.record(z.string(), z.boolean()).nullish(),
  bundleExpandedChildren: z
    .record(z.string(), z.array(ReturnableItemSchema))
    .nullish(),
  currentStep: createEnum(DraftReturnStep).nullish(),
  inProgressFlowWalk: z.any().nullish(),
  returnTogetherPropertyNames: z.array(z.string()).nullish(),
  useShopifyNativeBundles: z.boolean().nullish(),
  bundleReturnMode: z.string().nullish(),
});
export const DraftReturnSchema: z.ZodType<DraftReturn> = DraftReturnSchema_;
export interface DraftReturn extends z.infer<typeof DraftReturnSchema_> {}

export type ReturnRequest = DraftReturn;
